import type { ReactNode } from 'react';

/**
 * Einzige Quelle der Wahrheit fuer die Navigation des Operator-Konsole.
 *
 * Dieselbe Liste speist (a) die persistente Sidebar im AppShell und
 * (b) die Eintraege der globalen Command-Palette (Ctrl/Cmd+K). So bleibt
 * "was navigierbar ist" konsistent zwischen Chrome und Palette.
 */
export interface NavItem {
  /** Stabile id (auch fuer Command-Palette-Eintrag). */
  id: string;
  /** Sichtbares Label (DE — die Konsole ist operator-only, DE-first). */
  label: string;
  /** Ziel-Route (react-router). */
  to: string;
  /** Zusaetzliche Suchbegriffe fuers Command-Palette-Matching. */
  keywords?: string[];
  /** Optionales Badge-Element fuer die Sidebar (z. B. Zaehler). */
  badge?: ReactNode;
}

/**
 * Haupt-Navigation. Reihenfolge spiegelt den Operator-Workflow:
 * Tenants → offene Plan-Aenderungen → Audit → Jobs → CTI → Federation →
 * Marketplace-Fleet (vgl. die urspruengliche Top-Nav in App.tsx).
 *
 * `badge` wird in App.tsx gesetzt (die Badge-Komponenten pollen selbst), damit
 * dieses Modul frei von JSX/React-Komponenten-Importen bleibt.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    id: 'customers',
    label: 'Kunden',
    to: '/customers',
    keywords: ['kunde', 'kunden', 'customer', 'firma', 'company', 'stammdaten', 'billing'],
  },
  { id: 'tenants', label: 'Deployments', to: '/tenants', keywords: ['mandanten', 'tenant', 'tenants', 'deployment'] },
  {
    id: 'messages',
    label: 'Nachrichtencenter',
    to: '/messages',
    keywords: ['nachricht', 'nachrichten', 'message', 'ankuendigung', 'announcement', 'wartung', 'stoerung', 'incident', 'maintenance', 'mandanten'],
  },
  {
    id: 'contracts',
    label: 'Vertragsmanagement',
    to: '/contracts',
    keywords: ['vertrag', 'vertraege', 'contract', 'vertragsmanagement', 'billing', 's3', 'quota', 'lizenz', 'license'],
  },
  {
    id: 'billing',
    label: 'Abrechnung',
    to: '/billing',
    keywords: ['abrechnung', 'billing', 'kosten', 'preise', 'preisliste', 'rechnung', 'invoice', 'marge', 'deckungsbeitrag', 'periode'],
  },
  {
    id: 'contexts',
    label: 'MSP-Kontexte',
    to: '/contexts',
    keywords: ['kontext', 'context', 'gruppe', 'gruppierung', 'msp', 'provisioning'],
  },
  {
    id: 'brands',
    label: 'Marken',
    to: '/brands',
    keywords: ['brand', 'marke', 'white-label', 'whitelabel', 'partner', 'theming'],
  },
  {
    id: 'plan-changes',
    label: 'Plan-Änderungen',
    to: '/plan-changes',
    keywords: ['plan', 'change', 'request', 'tarif', 'upgrade', 'downgrade'],
  },
  {
    id: 'orders',
    label: 'Bestellungen',
    to: '/orders',
    keywords: ['order', 'bestellung', 'paket', 'package', 'homepage', 'anfrage'],
  },
  { id: 'audit', label: 'Audit-Log', to: '/audit', keywords: ['audit', 'log', 'protokoll', 'history'] },
  { id: 'jobs', label: 'Jobs', to: '/jobs', keywords: ['awx', 'ansible', 'job', 'automation'] },
  {
    id: 'cti-deployments',
    label: 'CTI-Deployments',
    to: '/cti-deployments',
    keywords: ['cti', 'threat', 'intel', 'deployment'],
  },
  {
    id: 'federation',
    label: 'Federation',
    to: '/federation',
    keywords: ['federation', 'target', 'cross-tenant'],
  },
  {
    id: 'credentials',
    label: 'Zugangsdaten',
    to: '/credentials',
    keywords: ['zugangsdaten', 'credentials', 'secrets', 'openbao', 'core-networks', 'dns', 'api-key', 'token', 'integration', 'zugang'],
  },
  {
    id: 'app-requests',
    label: 'App-Anfragen',
    to: '/app-requests',
    keywords: ['app', 'anfrage', 'request', 'marketplace', 'self-service', 'freigabe', 'deploy'],
  },
  {
    id: 'app-marketplace',
    label: 'App Marketplace',
    to: '/app-marketplace',
    keywords: ['marketplace', 'apps', 'katalog', 'catalog', 'store'],
  },
  {
    id: 'marketplace-fleet',
    label: 'Marketplace-Fleet',
    to: '/marketplace-fleet',
    keywords: ['marketplace', 'fleet', 'apps', 'coverage'],
  },
  {
    id: 'service-versions',
    label: 'Service-Versionen',
    to: '/service-versions',
    keywords: ['version', 'versionen', 'drift', 'dienste', 'services', 'katalog', 'aktuell', 'veraltet'],
  },
];

/**
 * Wichtige Aktions-Routen (Wizards), die nicht in der Sidebar stehen, aber
 * als Command-Palette-Aktionen schnell erreichbar sein sollen.
 */
export interface NavAction {
  id: string;
  label: string;
  to: string;
  keywords?: string[];
}

export const NAV_ACTIONS: NavAction[] = [
  {
    id: 'customer-new',
    label: 'Neuen Kunden anlegen',
    to: '/customers',
    keywords: ['create', 'neu', 'kunde', 'customer', 'firma', 'stammdaten'],
  },
  {
    id: 'tenant-new',
    label: 'Neues Deployment anlegen',
    to: '/tenants/new',
    keywords: ['create', 'neu', 'tenant', 'mandant', 'deployment', 'wizard'],
  },
  {
    id: 'cti-new',
    label: 'CTI-Deployment anlegen',
    to: '/cti-deployments/new',
    keywords: ['create', 'neu', 'cti', 'deployment'],
  },
  {
    id: 'message-new',
    label: 'Plattform-Nachricht verfassen',
    to: '/messages',
    keywords: ['create', 'neu', 'nachricht', 'message', 'wartung', 'stoerung', 'ankuendigung'],
  },
];
